import React, { useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Home, Users, PlusSquare, User, ShieldCheck, LogOut, MessageSquare, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import NotificationsBell from "@/components/NotificationsBell";

const navItem =
  "flex flex-col items-center justify-center gap-1 text-[10px] uppercase tracking-[0.18em] text-white/40 hover:text-white/90 transition py-2 px-3";
const activeClass = "text-[#d4af37]";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isAdmin = user && user.role === "admin";

  // Hide the fixed bottom nav while an input has focus so the iOS on-screen
  // keyboard never covers the chat / comment input.
  useEffect(() => {
    const taggable = (el) => el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA");
    const onFocus = (e) => {
      if (taggable(e.target) && e.target.type !== "file") {
        document.body.classList.add("kb-open");
      }
    };
    const onBlur = () => document.body.classList.remove("kb-open");
    document.addEventListener("focusin", onFocus);
    document.addEventListener("focusout", onBlur);
    return () => {
      document.removeEventListener("focusin", onFocus);
      document.removeEventListener("focusout", onBlur);
      document.body.classList.remove("kb-open");
    };
  }, []);

  return (
    <div className="App grain min-h-screen">
      {/* Top bar */}
      <header className="glass sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <button
            onClick={() => nav("/feed")}
            className="flex items-center gap-3"
            data-testid="logo-home"
          >
            <span className="w-8 h-8 rounded-full border border-[#d4af37]/60 flex items-center justify-center">
              <span className="text-[#d4af37] font-serif text-base">L</span>
            </span>
            <span className="font-serif text-xl tracking-wider">
              Lumière <span className="text-white/40">Club</span>
            </span>
          </button>
          <div className="flex items-center gap-2">
            {user && <NotificationsBell />}
            {isAdmin && (
              <NavLink
                to="/admin"
                data-testid="nav-admin-top"
                className={({ isActive }) =>
                  `hidden sm:flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-[0.2em] border border-white/10 hover:border-[#d4af37]/50 transition ${
                    isActive ? "border-[#d4af37] text-[#d4af37]" : "text-white/70"
                  }`
                }
              >
                <ShieldCheck size={14} strokeWidth={1.5} />
                Concierge
              </NavLink>
            )}
            <button
              onClick={async () => {
                await logout();
                nav("/login");
              }}
              data-testid="logout-btn"
              className="text-white/50 hover:text-white/90 p-2"
              title="Logout"
            >
              <LogOut size={18} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-5xl mx-auto px-5 sm:px-8 pb-28 pt-6">{children}</main>

      {/* Bottom nav (mobile primary) */}
      <nav className="app-bottom-nav fixed bottom-0 inset-x-0 z-40 glass border-t border-white/10">
        <div className="max-w-5xl mx-auto px-2 grid grid-cols-5">
          <NavLink to="/feed" className={({ isActive }) => `${navItem} ${isActive ? activeClass : ""}`} data-testid="nav-feed">
            <Home size={20} strokeWidth={1.5} />
            <span>Feed</span>
          </NavLink>
          <NavLink to="/groups" className={({ isActive }) => `${navItem} ${isActive ? activeClass : ""}`} data-testid="nav-groups">
            <Sparkles size={20} strokeWidth={1.5} />
            <span>Groups</span>
          </NavLink>
          <NavLink to="/compose" className={({ isActive }) => `${navItem} ${isActive ? activeClass : ""}`} data-testid="nav-compose">
            <PlusSquare size={20} strokeWidth={1.5} />
            <span>Post</span>
          </NavLink>
          <NavLink to="/messages" className={({ isActive }) => `${navItem} ${isActive ? activeClass : ""}`} data-testid="nav-messages">
            <MessageSquare size={20} strokeWidth={1.5} />
            <span>DM</span>
          </NavLink>
          <NavLink to="/profile" className={({ isActive }) => `${navItem} ${isActive ? activeClass : ""}`} data-testid="nav-profile">
            <User size={20} strokeWidth={1.5} />
            <span>Profile</span>
          </NavLink>
        </div>
      </nav>
    </div>
  );
}
